import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { AuthError } from "@/auth/types";
import { Button } from "@/components/ui/button";

import { FormAlert, PasswordField, useAuthErrorText } from "./components";
import { MIN_PASSWORD_LENGTH, PasswordStrengthMeter } from "./PasswordStrength";

interface PasswordChangeFormProps {
  /** Ask for the current (or temporary) password too. */
  withCurrent: boolean;
  currentLabel?: string;
  submitLabel?: string;
  onSubmit: (input: { currentPassword: string; newPassword: string }) => Promise<void>;
}

/** New password twice + strength meter (≥ 12 characters) — used by reset, first login, profile. */
export function PasswordChangeForm({
  withCurrent,
  currentLabel,
  submitLabel,
  onSubmit,
}: PasswordChangeFormProps) {
  const { t } = useTranslation("auth");
  const errorText = useAuthErrorText();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ next?: string; again?: string }>({});

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const errors: { next?: string; again?: string } = {};
    if (next.length < MIN_PASSWORD_LENGTH) {
      errors.next = t("strength.too_short", { count: MIN_PASSWORD_LENGTH - next.length });
    }
    if (again !== next) {
      errors.again = t("change.mismatch");
    }
    setFieldError(errors);
    if (errors.next || errors.again || pending) {
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onSubmit({ currentPassword: current, newPassword: next });
    } catch (err: unknown) {
      if (err instanceof AuthError && err.key === "weak_password") {
        setFieldError({ next: errorText(err) });
      } else {
        setError(errorText(err));
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="flex flex-col gap-4"
      noValidate
      data-slot="password-change"
    >
      {withCurrent ? (
        <PasswordField
          label={currentLabel ?? t("change.current_profile")}
          name="current"
          autoComplete="current-password"
          value={current}
          onChange={(event) => {
            setCurrent(event.target.value);
          }}
        />
      ) : null}
      <PasswordField
        label={t("change.new")}
        name="new"
        autoComplete="new-password"
        hint={t("strength.rule")}
        value={next}
        error={fieldError.next ?? null}
        onChange={(event) => {
          setNext(event.target.value);
        }}
      />
      <PasswordStrengthMeter password={next} />
      <PasswordField
        label={t("change.confirm")}
        name="confirm"
        autoComplete="new-password"
        value={again}
        error={fieldError.again ?? null}
        onChange={(event) => {
          setAgain(event.target.value);
        }}
      />
      <FormAlert message={error} />
      <Button type="submit" size="lg" disabled={pending}>
        {submitLabel ?? t("change.submit")}
      </Button>
    </form>
  );
}
